import { z } from "zod";

/**
 * Money is ALWAYS integer minor units (halalas). Never a float, never a string.
 * 249.00 SAR is `24900`.
 */
export const MoneyHalalasSchema = z.number().int().nonnegative();
export type MoneyHalalas = z.infer<typeof MoneyHalalasSchema>;

/** Signed money, for deltas only. Still integer halalas. */
export const SignedMoneyHalalasSchema = z.number().int();
export type SignedMoneyHalalas = z.infer<typeof SignedMoneyHalalasSchema>;

/** ISO-8601 UTC instant in transport. */
export const IsoTimestampSchema = z.string().datetime();
export type IsoTimestamp = z.infer<typeof IsoTimestampSchema>;

export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export type IsoDate = z.infer<typeof IsoDateSchema>;

/**
 * Every user-facing string crossing a boundary. BOTH translations required —
 * a missing translation is a type error, not a runtime fallback.
 */
export const LocalizedTextSchema = z.object({
  ar: z.string().min(1),
  en: z.string().min(1),
});
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

export const LocaleSchema = z.enum(["ar", "en"]);
export type Locale = z.infer<typeof LocaleSchema>;

export const UuidSchema = z.string().uuid();

/**
 * Crockford base32: `0123456789ABCDEFGHJKMNPQRSTVWXYZ`. No I, L, O or U — the four that a
 * shopper reading a reference off a screen confuses with 1, 1, 0 and V.
 */
const CROCKFORD_BASE32 = "[0-9ABCDEFGHJKMNPQRSTVWXYZ]";

/**
 * The format references are MINTED in today: `RTN-` plus ten Crockford base32 characters,
 * e.g. `RTN-4Z7KQ2M9XB`. ~2^50 of keyspace, because the reference plus a phone number is
 * the only credential on the tracking page and a sequential `RTN-#####` was enumerable.
 *
 * Use this to validate something we generated. Do NOT use it to validate shopper INPUT —
 * see {@link ReturnReferenceSchema}.
 */
export const MintedReturnReferenceSchema = z
  .string()
  .regex(new RegExp(`^RTN-${CROCKFORD_BASE32}{10}$`));
export type MintedReturnReference = z.infer<typeof MintedReturnReferenceSchema>;

/**
 * Shopper-facing return reference, as it arrives in a request.
 *
 * Deliberately still permissive, and NOT pinned to {@link MintedReturnReferenceSchema},
 * for two reasons:
 *
 *  1. It is shopper INPUT on the tracking form. A typo'd reference must reach the lookup
 *     and come back as the same generic not-found answer a wrong phone gets. Rejecting it
 *     at the schema as `invalid_request` would answer a different HTTP status for a
 *     malformed reference than for an unknown one — which is an enumeration oracle, and
 *     exactly what the long random reference was introduced to close.
 *  2. References minted before the format changed are in the corpus as `RTN-#####`
 *     (`RTN-88291`) and must stay resolvable for the life of those returns. Pinning here
 *     would make already-issued references un-lookupable.
 *
 * Validate the mint site, not the door: `MintedReturnReferenceSchema` on what the id
 * generator produces and on what is persisted.
 */
export const ReturnReferenceSchema = z.string().min(1);

/** Saudi mobile number. Kept permissive: fixture and store formats vary (05xxxxxxxx, +9665xxxxxxxx). */
export const PhoneSchema = z.string().min(6).max(20);

export const AddressSchema = z.object({
  city: z.string().min(1),
  line1: z.string().min(1),
  line2: z.string().nullable().optional(),
  district: z.string().nullable().optional(),
  /** Saudi National Address short code. */
  nationalAddress: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().default("SA"),
});
export type Address = z.infer<typeof AddressSchema>;

/** Cursor pagination. No offsets (docs/06). */
export const PaginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().positive().max(100).optional(),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/**
 * The id `POST /api/public/uploads` hands back, and the ONLY thing the shopper sends to
 * name a photo. Pinned to `upl_` plus up to 64 URL-safe characters.
 *
 * This is a security boundary, not tidiness: the id is concatenated into a stored object
 * URL that warehouse staff later click. Unvalidated, `../` or a scheme or whitespace in it
 * is a path-traversal / URL-injection primitive pointed at an internal user. The character
 * class excludes `.` and `/` precisely so no traversal sequence can be spelled.
 */
export const UploadIdSchema = z.string().regex(/^upl_[A-Za-z0-9_-]{1,64}$/);
export type UploadId = z.infer<typeof UploadIdSchema>;
